import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import AchievementsSection from '../components/AchievementsSection';
import ManualDeductionsSection from '../components/ManualDeductionsSection';
import OffboardingLettersSection from '../components/OffboardingLettersSection';
import OffboardingSection from '../components/OffboardingSection';
import EmployeeDocumentsVault from '../components/EmployeeDocumentsVault';
import EmployeeRolesSection from '../components/EmployeeRolesSection';
import EmployeeAssetsSection from '../components/EmployeeAssetsSection';
import SignedOfferReviewSection from '../components/SignedOfferReviewSection';
import { ArrowLeft, Mail, Phone, Building, Briefcase, Calendar, IndianRupee, Shield,
  BadgeCheck, TrendingUp, UserX, Award, AlertTriangle, History, UserCog, Tag, MapPin,
} from 'lucide-react';

const seniorityFor = { L1: 'Junior', L2: 'Associate', L3: 'Mid', L4: 'Senior', L5: 'Lead/Principal' };
const inr = (n) => n == null ? '—' : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const roleLabels = { admin: 'Admin', hr: 'HR', reporting_manager: 'Reporting Manager', employee: 'Employee' };

export default function EmployeeDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isHr = (user?.roles || [user?.role]).some(r => ['admin', 'hr'].includes(r));

  const [emp, setEmp] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [deptManagers, setDeptManagers] = useState([]);
  const [reviseOpen, setReviseOpen] = useState(false);
  const [termOpen, setTermOpen] = useState(false);
  const [rmOpen, setRmOpen] = useState(false);
  const [tagOpen, setTagOpen] = useState(false);
  const [reviseForm, setReviseForm] = useState({ new_annual_ctc: '', effective_from: new Date().toISOString().slice(0, 10), reason: '' });
  const [termForm, setTermForm] = useState({ reason: '', last_working_day: new Date().toISOString().slice(0, 10) });
  const [rmForm, setRmForm] = useState({ mode: 'dropdown', reporting_manager_id: '', email: '', reason: '' });
  const [tagForm, setTagForm] = useState({ custom_role_ids: [] });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/employees/${id}`);
      setEmp(data);
      if (isHr || user?.id === id) {
        try {
          const r = await api.get(`/employees/${id}/salary-revisions`);
          setRevisions(r.data);
        } catch { /* employee may not have revisions yet — ignore */ }
      }
      if (isHr) {
        try {
          const [cr, deps, brs] = await Promise.all([
            api.get('/custom-roles'),
            api.get('/departments'),
            api.get('/branches'),
          ]);
          setCustomRoles(cr.data);
          setDepartments(deps.data);
          setBranches(brs.data);
          if (data.department) {
            const m = await api.get(`/org/managers-for-department?department=${encodeURIComponent(data.department)}`);
            setDeptManagers(m.data);
          }
        } catch { /* ignore — non-critical sidebar data */ }
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      nav('/employees');
    }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const submitRevise = async () => {
    const newCtc = +reviseForm.new_annual_ctc;
    if (!newCtc || newCtc <= 0) return toast.error('Enter the revised annual CTC');
    if (emp?.annual_ctc && newCtc === emp.annual_ctc) return toast.error('New CTC is identical to current CTC');
    setSaving(true);
    try {
      await api.post(`/employees/${id}/revise-salary`, { ...reviseForm, new_annual_ctc: newCtc });
      toast.success(`Salary revised to ${inr(newCtc)} — employee notified by email`);
      setReviseOpen(false);
      setReviseForm({ new_annual_ctc: '', effective_from: new Date().toISOString().slice(0, 10), reason: '' });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const submitTerminate = async () => {
    if (!termForm.reason.trim()) return toast.error('Termination reason is required');
    if (!window.confirm(`Terminate ${emp.name}? They will lose access to most features. This action is logged.`)) return;
    setSaving(true);
    try {
      await api.post(`/employees/${id}/terminate`, termForm);
      toast.success(`${emp.name} has been terminated — notification email sent`);
      setTermOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  // Old `submitRoles` / `toggleRole` removed in Iter R — RBAC is now driven solely
  // by EmployeeRolesSection (which hits /api/users/{uid}/roles).

  const submitRm = async () => {
    const body = { reason: rmForm.reason || undefined };
    if (rmForm.mode === 'dropdown') {
      if (!rmForm.reporting_manager_id) return toast.error('Pick a reporting manager');
      body.reporting_manager_id = rmForm.reporting_manager_id;
    } else {
      if (!rmForm.email.trim()) return toast.error('Enter the new RM\'s email');
      body.reporting_manager_email = rmForm.email.trim();
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/employees/${id}/reporting-manager`, body);
      if (data.unchanged) toast.info('Already reporting to this manager');
      else toast.success(`${emp.name} now reports to ${data.new_rm_name} — email sent`);
      setRmOpen(false);
      setRmForm({ mode: 'dropdown', reporting_manager_id: '', email: '', reason: '' });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const submitTags = async () => {
    setSaving(true);
    try {
      await api.patch(`/employees/${id}/custom-roles`, tagForm);
      toast.success('Tags updated');
      setTagOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const updateBranch = async (branch_id) => {
    try {
      await api.patch(`/employees/${id}/branch`, { branch_id: branch_id === '__none__' ? null : branch_id });
      toast.success(branch_id === '__none__' ? 'Branch cleared' : 'Branch updated');
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleTag = (cid) => {
    setTagForm(f => ({
      ...f,
      custom_role_ids: f.custom_role_ids.includes(cid) ? f.custom_role_ids.filter(x => x !== cid) : [...f.custom_role_ids, cid],
    }));
  };

  if (!emp) return <div className="text-slate-400">Loading…</div>;

  const ss = emp.salary_structure;
  const canEdit = isHr;
  const canTerminate = isHr && !emp.terminated && emp.id !== user?.id;

  return (
    <div className="space-y-6" data-testid="employee-detail-page">
      <Link to="/employees" className="text-sm text-slate-500 inline-flex items-center gap-1 hover:text-[#3D4A8E]" data-testid="back-to-employees">
        <ArrowLeft className="w-4 h-4" /> Back to Employees
      </Link>

      <div className="bg-white border border-slate-200 rounded-2xl p-8" data-testid="employee-header">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-5">
            <div className="w-20 h-20 rounded-2xl bg-[#3D4A8E] text-white flex items-center justify-center font-heading text-3xl font-bold">
              {emp.name?.[0]?.toUpperCase()}
            </div>
            <div>
              <div className="font-heading text-2xl font-bold text-slate-900">{emp.name}</div>
              <div className="text-slate-500">{emp.title || '—'}</div>
              <div className="mt-2 flex gap-2 flex-wrap" data-testid="emp-detail-role-badges">
                {(emp.roles || [emp.role]).map(r => (
                  <span key={r} className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full bg-[#3D4A8E]/10 text-[#3D4A8E]">
                    <Shield className="w-3 h-3" /> {(roleLabels[r] || r)}
                  </span>
                ))}
                {(emp.custom_role_ids || []).map(cid => {
                  const cr = customRoles.find(c => c.id === cid);
                  if (!cr) return null;
                  return (
                    <span key={cid} className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: cr.color || '#3D4A8E' }}>
                      <Tag className="w-3 h-3" /> {cr.name}
                    </span>
                  );
                })}
                {emp.employee_id && (
                  <span className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200" data-testid="emp-detail-id">
                    {emp.employee_id}
                  </span>
                )}
                {emp.grade && (
                  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                    <Award className="w-3 h-3" /> {emp.grade} · {seniorityFor[emp.grade]}
                  </span>
                )}
                {emp.terminated && (
                  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full bg-red-50 text-red-700 border border-red-200" data-testid="terminated-badge">
                    <UserX className="w-3 h-3" /> Terminated
                  </span>
                )}
              </div>
            </div>
          </div>

          {canEdit && (
            <div className="flex gap-2 flex-wrap" data-testid="employee-detail-actions">
              {/* The old "Manage roles" dialog was removed in Iter R — see the
                  Roles & Permissions section below for the unified RBAC UI. */}
              <Dialog open={rmOpen} onOpenChange={(o) => { setRmOpen(o); if (o) setRmForm({ mode: 'dropdown', reporting_manager_id: emp.reporting_manager_id || '', email: '', reason: '' }); }}>
                <DialogTrigger asChild>
                  <Button data-testid="change-rm-button" variant="outline" disabled={emp.terminated}
                          className="rounded-full h-10 border-blue-300 text-blue-700 hover:bg-blue-50">
                    <UserCog className="w-4 h-4 mr-1.5" /> Change reporting manager
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md" data-testid="change-rm-dialog">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">Change reporting manager</DialogTitle>
                    <p className="text-sm text-slate-500">Pick from the {emp.department || '—'} team or enter any other RM&apos;s work email.</p>
                  </DialogHeader>
                  <div className="space-y-3 mt-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-semibold text-slate-600">New reporting manager *</Label>
                      <button type="button" data-testid="rm-mode-toggle"
                              onClick={() => setRmForm(f => ({ ...f, mode: f.mode === 'dropdown' ? 'email' : 'dropdown', reporting_manager_id: '', email: '' }))}
                              className="text-xs text-[#3D4A8E] hover:underline">
                        {rmForm.mode === 'dropdown' ? 'Enter email manually instead' : 'Pick from list instead'}
                      </button>
                    </div>
                    {rmForm.mode === 'dropdown' ? (
                      <Select value={rmForm.reporting_manager_id || undefined} onValueChange={(v) => setRmForm({ ...rmForm, reporting_manager_id: v })}>
                        <SelectTrigger data-testid="rm-select" className="rounded-xl"><SelectValue placeholder={deptManagers.length ? `${emp.department} team` : `No one in ${emp.department || '—'} yet`} /></SelectTrigger>
                        <SelectContent>
                          {deptManagers.filter(m => m.id !== emp.id).map(m => (
                            <SelectItem key={m.id} value={m.id}>
                              {m.name} · {m.is_manager ? m.role.replace('_',' ') : 'employee'}{m.title ? ` · ${m.title}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input data-testid="rm-email-input" type="email" value={rmForm.email} onChange={(e) => setRmForm({ ...rmForm, email: e.target.value })} placeholder="manager@lanvaus.com" className="rounded-xl" />
                    )}
                    <div>
                      <Label className="text-xs font-semibold text-slate-600">Reason (optional)</Label>
                      <Textarea data-testid="rm-reason" value={rmForm.reason} onChange={(e) => setRmForm({ ...rmForm, reason: e.target.value })} rows={2} placeholder="e.g. Reassigned to the Platform squad" className="rounded-xl" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setRmOpen(false)} className="rounded-full">Cancel</Button>
                    <Button data-testid="submit-rm" onClick={submitRm} disabled={saving} className="rounded-full bg-blue-600 hover:bg-blue-700">
                      {saving ? 'Updating…' : 'Update RM'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <Dialog open={tagOpen} onOpenChange={(o) => { setTagOpen(o); if (o) setTagForm({ custom_role_ids: emp.custom_role_ids || [] }); }}>
                <DialogTrigger asChild>
                  <Button data-testid="manage-tags-button" variant="outline" disabled={emp.terminated || customRoles.length === 0}
                          className="rounded-full h-10 border-purple-300 text-purple-700 hover:bg-purple-50" title={customRoles.length === 0 ? 'No custom roles created yet' : ''}>
                    <UserCog className="w-4 h-4 mr-1.5" /> Tags
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md" data-testid="manage-tags-dialog">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">Custom role tags</DialogTitle>
                    <p className="text-sm text-slate-500">Display-only labels that show up on this employee&apos;s profile. Manage the list under <b>Custom Roles</b>.</p>
                  </DialogHeader>
                  <div className="space-y-2 mt-2 max-h-72 overflow-auto">
                    {customRoles.length === 0 && <div className="text-sm text-slate-400 text-center py-6">No custom roles defined yet.</div>}
                    {customRoles.map(c => {
                      const checked = tagForm.custom_role_ids.includes(c.id);
                      return (
                        <label key={c.id} className={`flex items-center gap-3 p-2.5 rounded-xl border cursor-pointer ${checked ? 'border-[#3D4A8E] bg-[#3D4A8E]/5' : 'border-slate-200 hover:border-slate-300'}`}>
                          <input type="checkbox" checked={checked} onChange={() => toggleTag(c.id)} data-testid={`tag-checkbox-${c.id}`} className="w-4 h-4 accent-[#3D4A8E]" />
                          <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: c.color || '#3D4A8E' }}>
                            <Tag className="w-3 h-3" /> {c.name}
                          </span>
                          {c.description && <span className="text-xs text-slate-500 truncate">{c.description}</span>}
                        </label>
                      );
                    })}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setTagOpen(false)} className="rounded-full">Cancel</Button>
                    <Button data-testid="submit-tags" onClick={submitTags} disabled={saving} className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
                      {saving ? 'Saving…' : 'Save tags'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <Dialog open={reviseOpen} onOpenChange={setReviseOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="revise-salary-button" disabled={emp.terminated}
                          className="rounded-full bg-emerald-600 hover:bg-emerald-700 h-10">
                    <TrendingUp className="w-4 h-4 mr-1.5" /> Revise salary
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md" data-testid="revise-salary-dialog">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">Revise salary</DialogTitle>
                    <p className="text-sm text-slate-500">Current CTC: <b>{inr(emp.annual_ctc)}</b></p>
                  </DialogHeader>
                  <div className="space-y-3 mt-2">
                    <div>
                      <Label className="text-xs font-semibold text-slate-600">New annual CTC (₹) *</Label>
                      <Input data-testid="revise-ctc" type="number" value={reviseForm.new_annual_ctc} onChange={(e) => setReviseForm({ ...reviseForm, new_annual_ctc: e.target.value })} className="rounded-xl mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-600">Effective from *</Label>
                      <Input data-testid="revise-effective" type="date" value={reviseForm.effective_from} onChange={(e) => setReviseForm({ ...reviseForm, effective_from: e.target.value })} className="rounded-xl mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-600">Reason</Label>
                      <Textarea data-testid="revise-reason" value={reviseForm.reason} onChange={(e) => setReviseForm({ ...reviseForm, reason: e.target.value })} rows={2} placeholder="e.g. Annual cycle, promotion to L4…" className="rounded-xl mt-1" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setReviseOpen(false)} className="rounded-full">Cancel</Button>
                    <Button data-testid="submit-revise" onClick={submitRevise} disabled={saving} className="rounded-full bg-emerald-600 hover:bg-emerald-700">
                      {saving ? 'Saving…' : 'Revise salary'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {canTerminate && (
                <Dialog open={termOpen} onOpenChange={setTermOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="terminate-button" variant="outline"
                            className="rounded-full h-10 border-red-300 text-red-600 hover:bg-red-50">
                      <UserX className="w-4 h-4 mr-1.5" /> Terminate
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md" data-testid="terminate-dialog">
                    <DialogHeader>
                      <DialogTitle className="font-heading text-2xl text-red-700">Terminate employment</DialogTitle>
                      <p className="text-sm text-slate-500">This will revoke most HRMS access. {emp.name} will retain login to their <b>personal email</b> for accessing payslips and HR contact only.</p>
                    </DialogHeader>
                    <div className="space-y-3 mt-2">
                      <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-900 flex gap-2">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        A termination email will be sent. This action is logged.
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-slate-600">Reason *</Label>
                        <Textarea data-testid="term-reason" value={termForm.reason} onChange={(e) => setTermForm({ ...termForm, reason: e.target.value })} rows={3} placeholder="Reason for termination" className="rounded-xl mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-slate-600">Last working day</Label>
                        <Input data-testid="term-lwd" type="date" value={termForm.last_working_day} onChange={(e) => setTermForm({ ...termForm, last_working_day: e.target.value })} className="rounded-xl mt-1" />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setTermOpen(false)} className="rounded-full">Cancel</Button>
                      <Button data-testid="submit-terminate" onClick={submitTerminate} disabled={saving} className="rounded-full bg-red-600 hover:bg-red-700">
                        {saving ? 'Processing…' : 'Confirm termination'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          )}
        </div>

        {emp.terminated && (
          <div className="mt-5 bg-red-50 border border-red-200 rounded-xl p-4" data-testid="termination-info">
            <div className="text-xs font-bold uppercase tracking-wider text-red-700">Termination details</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2 text-sm">
              <div><span className="text-red-700/70">Last working day:</span> <b>{emp.last_working_day || '—'}</b></div>
              <div><span className="text-red-700/70">Terminated by:</span> <b>{emp.terminated_by_name || '—'}</b></div>
              <div><span className="text-red-700/70">When:</span> <b>{emp.terminated_at ? new Date(emp.terminated_at).toLocaleDateString() : '—'}</b></div>
              <div className="md:col-span-3"><span className="text-red-700/70">Reason:</span> <b>{emp.termination_reason || '—'}</b></div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
          {[
            { icon: Mail, label: 'Work email', value: emp.email },
            { icon: Mail, label: 'Personal email', value: emp.personal_email || '—' },
            { icon: Phone, label: 'Phone', value: emp.phone || '—' },
            { icon: Building, label: 'Department', value: emp.department || 'Unassigned' },
            { icon: Briefcase, label: 'Title', value: emp.title || '—' },
            { icon: Calendar, label: 'Joined', value: emp.joined_at || '—' },
          ].map((it, i) => {
            const Icon = it.icon;
            return (
              <div key={i} className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center text-slate-500 border border-slate-200">
                  <Icon className="w-4 h-4" strokeWidth={1.8} />
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">{it.label}</div>
                  <div className="text-sm font-semibold text-slate-900 mt-0.5 truncate">{it.value}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Salary card (HR + self only) */}
      {(isHr || user?.id === emp.id) && ss && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="salary-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Compensation</div>
              <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1">{inr(emp.annual_ctc)} annual CTC</h2>
            </div>
            <IndianRupee className="w-6 h-6 text-slate-300" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Mini label="Monthly gross" v={inr(ss.monthly_gross)} />
            <Mini label="Monthly net" v={inr(ss.monthly_net)} accent="text-emerald-700" />
            <Mini label="Monthly TDS" v={inr(ss.monthly_tax)} accent="text-red-600" />
            <Mini label="Annual tax" v={inr(ss.annual_tax)} />
          </div>
        </div>
      )}

      {/* Salary revision history */}
      {(isHr || user?.id === emp.id) && revisions.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="salary-history">
          <div className="flex items-center gap-2 mb-4">
            <History className="w-4 h-4 text-slate-400" />
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Salary revision history</div>
          </div>
          <div className="space-y-2">
            {revisions.map(r => (
              <div key={r.id} data-testid={`revision-${r.id}`} className="flex items-start justify-between gap-3 p-3 rounded-xl border border-slate-100">
                <div>
                  <div className="font-medium text-slate-900">{inr(r.previous_ctc)} → <span className="text-emerald-700">{inr(r.new_ctc)}</span> <span className="text-xs text-slate-400">({r.delta >= 0 ? '+' : ''}{inr(r.delta)})</span></div>
                  <div className="text-xs text-slate-500 mt-0.5">Effective {r.effective_from} · By {r.by_name} on {new Date(r.created_at).toLocaleDateString()}</div>
                  {r.reason && <div className="text-sm text-slate-700 mt-1">{r.reason}</div>}
                </div>
                <BadgeCheck className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Achievements */}
      <AchievementsSection employeeId={emp.id} readonly={user?.id !== emp.id && !isHr} />

      {/* Roles & Permissions — Admin-only management; others read-only */}
      {(user?.role === 'admin' || user?.id === emp.id) && (
        <EmployeeRolesSection employeeId={emp.id} employeeName={emp.name} isPrimaryAdmin={!!emp.is_primary_admin} />
      )}

      {/* Signed offer letter (post-BGC workflow) — only shown when relevant */}
      {(isHr || user?.id === emp.id) && ['bgc_cleared', 'verified'].includes(emp.onboarding_status) && (
        <SignedOfferReviewSection employeeId={emp.id} canManage={isHr} onChanged={load} />
      )}

      {/* Documents vault — 5 folders synced both ways with the employee's own profile */}
      {(isHr || user?.id === emp.id) && (
        <EmployeeDocumentsVault employeeId={emp.id} canManage={isHr} isSelf={user?.id === emp.id} />
      )}

      {/* Branch assignment */}
      {(isHr || user?.id === emp.id) && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="branch-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> Branch / Location</div>
              <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1">{emp.branch_id ? (branches.find(b => b.id === emp.branch_id)?.name || 'Branch') : 'Not assigned'}</h2>
              <p className="text-xs text-slate-500 mt-1">This address appears on offer letters and monthly payslips.</p>
            </div>
            {isHr && (
              <div className="w-full md:w-72" data-testid="branch-picker-wrap">
                <Select value={emp.branch_id || '__none__'} onValueChange={updateBranch}>
                  <SelectTrigger data-testid="branch-picker" className="rounded-xl"><SelectValue placeholder={branches.length ? 'Pick a branch' : 'No branches yet'} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— No branch —</SelectItem>
                    {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}{b.type === 'headquarters' ? ' (HQ)' : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Manual deductions */}
      {(isHr || user?.id === emp.id) && (
        <ManualDeductionsSection employeeId={emp.id} canEdit={isHr && !emp.terminated} />
      )}

      {/* Company assets assigned to this employee */}
      {(isHr || user?.id === emp.id || user?.id === emp.reporting_manager_id) && (
        <EmployeeAssetsSection employeeId={emp.id} employeeName={emp.name} canManage={isHr} terminated={!!emp.terminated} />
      )}

      {/* Offboarding workflow (HR can initiate / employee can resign) */}
      {(isHr || user?.id === emp.id) && !emp.terminated && (
        <OffboardingSection employeeId={emp.id} employeeName={emp.name} isSelf={user?.id === emp.id} canManage={isHr} onChanged={load} />
      )}

      {/* Offboarding letters (visible to HR, RM-of-employee, and self once terminated) */}
      {emp.terminated && (isHr || user?.id === emp.id || user?.id === emp.reporting_manager_id) && (
        <OffboardingLettersSection employeeId={emp.id} employeeName={emp.name} terminated={emp.terminated} />
      )}
    </div>
  );
}

const Mini = ({ label, v, accent = 'text-slate-900' }) => (
  <div>
    <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">{label}</div>
    <div className={`font-heading text-xl font-bold mt-1 ${accent}`}>{v}</div>
  </div>
);
